import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { del, HTTPError, post } from "common/helpers/HTTP";
import type {
  AgencyPlan,
  DetailedWorkspace,
  Promotion,
  WorkspaceAccount,
} from "common/types";

import getQueryKeys from "./getQueryKeys";

async function assignLicenseRequest(
  workspaceId: string,
  accountId: string,
  abortSignal?: AbortSignal,
): Promise<DetailedWorkspace> {
  return (
    await post(
      `workspaces/${workspaceId}/accounts/${accountId}/license`,
      undefined,
      abortSignal,
    )
  ).workspace;
}

async function assignLicensesRequest(
  workspaceId: string,
  accountIds: string[],
): Promise<{ workspace: DetailedWorkspace; accounts: WorkspaceAccount[] }> {
  const {
    workspace: { accounts, ...detailedWorkspace },
  } = await post(`workspaces/${workspaceId}/accounts/license`, {
    account_ids: accountIds,
  });

  return { workspace: detailedWorkspace, accounts };
}

async function removeLicenseRequest(workspaceId: string, accountId: string) {
  await del(`workspaces/${workspaceId}/accounts/${accountId}/license`);
}

async function removeLicenseCancellationRequest(
  workspaceId: string,
  accountId: string,
): Promise<DetailedWorkspace> {
  return (
    await post(
      `workspaces/${workspaceId}/accounts/${accountId}/license/reactivate`,
    )
  ).workspace;
}

async function setLicenseCountRequest(
  workspaceId: string,
  count: number,
  planId: string,
  accountsToCancel: string[],
): Promise<{ workspace: DetailedWorkspace }> {
  const { workspace } = await post(`workspaces/${workspaceId}/licenses`, {
    count,
    item_price_id: planId,
    account_licenses_to_cancel: accountsToCancel,
  });

  return { workspace };
}

async function setLifetimeLicenseCountRequest(
  workspaceId: string,
  count: number,
  accountsToCancel: string[],
  source: "appsumo" | "ltd" | "agency",
): Promise<{ workspace: DetailedWorkspace; promotion: AgencyPlan }> {
  const { workspace, promotion } = await post(
    `workspaces/${workspaceId}/licenses/lifetime`,
    {
      count,
      source,
      account_ids: accountsToCancel,
    },
  );

  return { workspace, promotion };
}

export default function useLicenseMutations() {
  const queryClient = useQueryClient();

  const { id: workspaceId } = useSelectedWorkspaceContext();

  const {
    updateCachedWorkspaceDetails,
    updateCachedWorkspaceList,
    updateCachedWorkspaceAccounts,
  } = useWorkspaceCacheUpdate();

  const { mutateAsync: assignLicense, isPending: isAssigningLicense } =
    useMutation({
      mutationFn: ({
        accountId,
        abortSignal,
      }: {
        accountId: string;
        abortSignal?: AbortSignal;
      }) => assignLicenseRequest(workspaceId, accountId, abortSignal),
      onSuccess: (updatedWorkspace, { accountId }) => {
        const addedAccountLicense = updatedWorkspace.licenses.find(
          (license) => license.account_id === accountId,
        );

        updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
          const targetAccount = draftAccounts.find(
            ({ id }) => id === accountId,
          );
          if (targetAccount) {
            targetAccount.license = addedAccountLicense;
          }
        });

        const detailedKey = getQueryKeys(workspaceId).workspaceKeys.base;
        if (queryClient.getQueryData<DetailedWorkspace>(detailedKey)) {
          queryClient.setQueryData(detailedKey, updatedWorkspace);
        }
      },
    });

  const { mutateAsync: removeLicense, isPending: isRemovingLicense } =
    useMutation({
      mutationFn: ({ accountId }: { accountId: string }) =>
        removeLicenseRequest(workspaceId, accountId),
      onSuccess: (_, { accountId }) => {
        updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
          const targetAccount = draftAccounts.find(
            ({ id }) => id === accountId,
          );

          if (targetAccount) {
            delete targetAccount.license;
          }
        });

        updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
          const { licenses } = draftWorkspace;

          if (licenses) {
            const targetLicense = licenses.find(
              ({ account_id }) => account_id === accountId,
            );

            if (targetLicense) {
              targetLicense.account_id = undefined;
            }
          }
        });
      },
      onError: () => toast.error("Failed to remove seat"),
    });

  const {
    mutateAsync: removeLicenseCancellation,
    isPending: isRemovingLicenseCancellation,
  } = useMutation({
    mutationFn: ({ accountId }: { accountId: string }) =>
      removeLicenseCancellationRequest(workspaceId, accountId),
    onSuccess: (workspace, { accountId }) => {
      updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
        const targetAccount = draftAccounts.find(({ id }) => id === accountId);

        if (targetAccount?.license) {
          targetAccount.license.expires_at = 0;
          targetAccount.license.status = "active";
        }
      });

      updateCachedWorkspaceDetails(workspaceId, (draftWorkspace) => {
        const { licenses } = draftWorkspace;

        if (licenses) {
          const targetLicense = licenses.find(
            ({ account_id }) => account_id === accountId,
          );

          if (targetLicense) {
            targetLicense.expires_at = 0;
            targetLicense.status = "active";
          }
        }
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(
          workspace.subscription.id,
        ),
      });
    },
    onError: () => toast.error("Failed to remove license cancellation"),
  });

  const {
    mutateAsync: setLicenseCount,
    isPending: isSettingLicenseCount,
    error: setLicenseCountError,
  } = useMutation({
    mutationFn: ({
      count,
      planId,
      accountsToCancel,
    }: {
      count: number;
      planId: string;
      accountsToCancel: string[];
    }) => setLicenseCountRequest(workspaceId, count, planId, accountsToCancel),
    onSuccess: ({ workspace }) => {
      updateCachedWorkspaceDetails(workspaceId, () => {
        return workspace;
      });
      updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
        draftWorkspace.subscription_status = "active";
        draftWorkspace.license_count = workspace.license_count;
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(
          workspace.subscription.id,
        ),
      });
    },
  });

  const { mutateAsync: assignLicenses, isPending: isAssigningLicenses } =
    useMutation({
      mutationFn: ({ accountIds }: { accountIds: string[] }) =>
        assignLicensesRequest(workspaceId, accountIds),
      onSuccess: ({ workspace, accounts }) => {
        updateCachedWorkspaceDetails(workspaceId, () => {
          return workspace;
        });
        updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
          draftWorkspace.license_count = workspace.license_count;
        });
        updateCachedWorkspaceAccounts(workspaceId, () => {
          return accounts;
        });
      },
      onError: (error) =>
        toast.error(
          error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Failed to assign seats",
        ),
    });

  const {
    mutateAsync: setLifetimeLicenseCount,
    isPending: isSettingLifetimeLicenseCount,
    error: setLifetimeLicenseCountError,
  } = useMutation({
    mutationFn: ({
      workspaceId,
      count,
      source,
      accountsToCancel,
    }: {
      workspaceId: string;
      count: number;
      source: "ltd" | "agency" | "appsumo";
      accountsToCancel: string[];
    }) =>
      setLifetimeLicenseCountRequest(
        workspaceId,
        count,
        accountsToCancel,
        source,
      ),
    onSuccess: ({ workspace, promotion }) => {
      updateCachedWorkspaceDetails(workspace.id, (draftWorkspace) => {
        draftWorkspace.license_count = workspace.license_count;
        draftWorkspace.agency_license_count = workspace.agency_license_count;
        draftWorkspace.lifetime_license_count =
          workspace.lifetime_license_count;
      });
      updateCachedWorkspaceList(workspace.id, (draftWorkspace) => {
        draftWorkspace.license_count = workspace.license_count;
        draftWorkspace.agency_license_count = workspace.agency_license_count;
        draftWorkspace.lifetime_license_count =
          workspace.lifetime_license_count;
      });
      queryClient.setQueryData<Promotion[]>(
        CUSTOMER_QUERY_KEYS.promotions,
        (prevPromotions) =>
          produce(prevPromotions, (promotionsDraft) => {
            for (let i = 0; i < promotionsDraft.length; i += 1) {
              const draftPromotion = promotionsDraft[i];
              if (draftPromotion.type === promotion.type) {
                promotionsDraft[i] = promotion;
                return promotionsDraft;
              }
            }
          }),
      );
    },
    onError: (error) =>
      toast.error(
        error instanceof HTTPError && error.serverMessage
          ? error.serverMessage
          : "Failed to update subscription",
      ),
  });

  return {
    assignLicense,
    isAssigningLicense,

    removeLicense,
    isRemovingLicense,

    removeLicenseCancellation,
    isRemovingLicenseCancellation,

    setLicenseCount,
    isSettingLicenseCount,
    setLicenseCountError,

    assignLicenses,
    isAssigningLicenses,
    setLifetimeLicenseCount,

    isSettingLifetimeLicenseCount,
    setLifetimeLicenseCountError,
  };
}
